import { apiClient } from './client'

export type ProcurementRequestItemStatus = 'PENDING' | 'ANSWERED' | 'REFUSED' | 'NO_RESPONSE'
export type ProcurementRequestStatus = 'OPEN' | 'COMPLETED' | 'ARCHIVED'

export interface ProcurementRequestItem {
  id: string
  // null — позиция удалена вместе с заявкой (BOM-строка осиротела). Снапшот
  // partNumber/brand/quantity ниже сохраняется, квоту к строке не привязать.
  requestItemId: string | null
  partNumber: string
  brand: string | null
  quantity: number
  status: ProcurementRequestItemStatus
  responsePrice: string | null
  responseCurrency: string | null
  responseNote: string | null
  linkedQuoteId: string | null
}

export interface ProcurementRequestSummary {
  id: string
  fileCode: string
  supplierName: string
  procurementUserId: string
  status: ProcurementRequestStatus
  note: string | null
  createdAt: string
  updatedAt: string
  procurementUser: { id: string; name: string | null; email: string }
  _count: { items: number }
  // items[].status — для расчёта прогресса; полные данные позиции — в карточке.
  items: { status: ProcurementRequestItemStatus; linkedQuoteId: string | null }[]
}

export interface ProcurementRequestDetail extends Omit<
  ProcurementRequestSummary,
  '_count' | 'items'
> {
  items: (ProcurementRequestItem & {
    requestItem: {
      id: string
      partNumber: string
      brand: string | null
      quantity: number
      status: string
      request: { id: string; requestNumber: string | null; clientId: string }
    } | null
    linkedQuote: {
      id: string
      price: string
      currency: string
      supplierName: string
    } | null
  })[]
}

/** Позиции, не вошедшие в BOM производителю: канал занят коллегой. */
export interface ManufacturerExcludedItem {
  requestItemId: string
  partNumber: string
  byName: string
  byRole: string
  at: string
}

export interface BulkCreateResult {
  requests: ProcurementRequestDetail[]
  // Отчёт по прямому запросу производителю (null — производитель не выбран).
  manufacturerReport: {
    requestId: string | null
    excluded: ManufacturerExcludedItem[]
  } | null
}

/** Поставщик в bulk-создании; requestItemIds — его персональный набор позиций
 *  (раскладка «Разложить по поставщикам»), не задан → все позиции запроса. */
export interface BulkSupplierInput {
  name: string
  requestItemIds?: string[]
}

export async function bulkCreateProcurementRequests(payload: {
  requestItemIds: string[]
  suppliers: BulkSupplierInput[]
  // Прямой запрос производителю (эксклюзив канала per-позиция).
  manufacturer?: { name: string }
  note?: string
}): Promise<BulkCreateResult> {
  const res = await apiClient.post<{ success: true; data: BulkCreateResult }>(
    '/procurement-requests/bulk',
    payload,
  )
  return res.data.data
}

export interface ProcurementRequestsPage {
  items: ProcurementRequestSummary[]
  hasMore: boolean
  nextCursor: string | null
}

export async function listProcurementRequests(
  params: {
    status?: ProcurementRequestStatus
    supplierName?: string
    search?: string
    limit?: number
    cursor?: string
  } = {},
): Promise<ProcurementRequestsPage> {
  const res = await apiClient.get<{ success: true; data: ProcurementRequestsPage }>(
    '/procurement-requests',
    { params },
  )
  return res.data.data
}

export interface ProcurementRequestStats {
  all: number
  open: number
  completed: number
  archived: number
}

/** Счётчики по статусам — не зависят от выбранного статус-фильтра. */
export async function getProcurementRequestStats(
  params: { search?: string; supplierName?: string } = {},
): Promise<ProcurementRequestStats> {
  const res = await apiClient.get<{ success: true; data: ProcurementRequestStats }>(
    '/procurement-requests/stats',
    { params },
  )
  return res.data.data
}

export async function getProcurementRequest(id: string): Promise<ProcurementRequestDetail> {
  const res = await apiClient.get<{ success: true; data: ProcurementRequestDetail }>(
    `/procurement-requests/${id}`,
  )
  return res.data.data
}

export async function patchProcurementRequestItem(
  requestId: string,
  itemId: string,
  data: { status?: ProcurementRequestItemStatus; responseNote?: string | null },
): Promise<ProcurementRequestItem> {
  const res = await apiClient.patch<{ success: true; data: ProcurementRequestItem }>(
    `/procurement-requests/${requestId}/items/${itemId}`,
    data,
  )
  return res.data.data
}

export async function deleteProcurementRequest(id: string): Promise<void> {
  await apiClient.delete(`/procurement-requests/${id}`)
}

export async function getSupplierSuggestions(): Promise<{ name: string; count: number }[]> {
  const res = await apiClient.get<{ success: true; data: { name: string; count: number }[] }>(
    '/procurement-requests/suppliers',
  )
  return res.data.data
}

/** Скрывает поставщика из подсказок навсегда (глобально). */
export async function hideSupplierSuggestion(name: string): Promise<void> {
  await apiClient.post('/procurement-requests/suppliers/hide', { name })
}

/** Снятие claim производителя с позиции — только РОЗ/ADMIN. */
export async function releaseManufacturerClaim(
  requestItemId: string,
): Promise<{ released: boolean; previousHolder: string }> {
  const res = await apiClient.delete<{
    success: true
    data: { released: boolean; previousHolder: string }
  }>(`/procurement-requests/manufacturer-claim/${requestItemId}`)
  return res.data.data
}

export async function uploadProcurementResponse(payload: {
  fileCode: string
  rows: {
    partNumber: string
    brand: string | null
    price: number
    currency: string
    note: string | null
  }[]
}): Promise<{
  fileCode: string
  answered: number
  refused: number
  unmatched: number
  skippedDuplicates: number
}> {
  const res = await apiClient.post<{
    success: true
    data: {
      fileCode: string
      answered: number
      refused: number
      unmatched: number
      skippedDuplicates: number
    }
  }>('/procurement-requests/upload-response', payload)
  return res.data.data
}

export interface ProcurementRequestMatchCandidate {
  id: string
  fileCode: string
  supplierName: string
  createdAt: string
  totalItems: number
  matchedItems: number
  // Позиции файла, найденные в запросе: по этому списку parseBatchResponse
  // делит загрузку — совпавшие идут в запрос, остальные обычным bulk-путём.
  matchedRequestItemIds: string[]
}

// Поиск открытого запроса-кандидата для Excel-файла без метки
// procurement-request:... — см. parseBatchResponse. Возвращает запрос того же
// поставщика с максимальным пересечением позиций либо null.
export async function findProcurementRequestCandidate(payload: {
  supplierName: string
  requestItemIds: string[]
}): Promise<ProcurementRequestMatchCandidate | null> {
  const res = await apiClient.post<{
    success: true
    data: ProcurementRequestMatchCandidate | null
  }>('/procurement-requests/match-candidate', payload)
  return res.data.data
}
